import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { FisheryNotice } from 'src/app/data-models/fishery-notice.model';
import { UserData } from 'src/app/data-models/user-models/user-data.model';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { CommonDataService } from 'src/app/services/storage/common-data.service';
import { FisheryNoticesService } from 'src/app/services/storage/fishery-notices.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';

@Component({
    selector: 'app-fishery-notices',
    templateUrl: './fishery-notices.component.html',
    styleUrls: ['./fishery-notices.component.css'],
    standalone: false
})
export class FisheryNoticesComponent implements OnDestroy {

  isDataLoaded: boolean = false; // flag that tells us if the HTML is ready to be rendered

  // data preambles
  userDetails: UserData = new UserData();
  fisheryNotices: FisheryNotice[] = [];
  noticeMetadata: FisheryNotice[] = [];
  currNotice: FisheryNotice = new FisheryNotice();

  // programmatical flags for which notices to display
  showInboxList: boolean = true;
  showAllNotices: boolean = false;
  showSavedNotices: boolean = false;
  showGillNetNotices: boolean = false;
  showSeineNotices: boolean = false;
  showTrollNotices: boolean = false;
  showCurrNotice: boolean = false;

  constructor(
    private authenticationService: AuthenticationService,
    private onlineStatusService: OnlineStatusService,
    private ref: ChangeDetectorRef,
    public sharedMethodsService: SharedMethodsService,
    public commonDataService: CommonDataService
  ) {

    this.authenticationService.getUserDetails().then(() => {
      this.userDetails = UserStorageService.currUser;
    }).then(() => {
      UserStorageService.getNoticeMetadata().then(() => {
        FisheryNoticesService.getAllNoticeDocs(this.userDetails.userEmail).then(() => {
          this.initialize();
        });
      });
    });

  }


  /*  ---------- Page Initialization Methods  ---------- */


  // retrieve fishery notice data from the common db
  initialize(): void {

    this.fisheryNotices = FisheryNoticesService.fisheryNotices.slice().reverse();
    this.noticeMetadata = UserStorageService.noticeMetadata.slice();
    this.putSaved();   
    this.updateNoticeData(); // cross reference noticeMetadata with the fisheryNotices data
    this.parseNotices(); // parse html for proper formatting

    //Keep the user on the specific fishery notice if they refresh
    let vn = localStorage.getItem('ViewedNotice');
    if (vn) {
      this.noticeSelected(this.fisheryNotices.find(fn => fn.noticeNumber == vn)!);
      this.inboxClick(localStorage.getItem('ViewedInbox') ?? 'allNotices');
    } 
    
    this.isDataLoaded = true; // set flag to render HTML
    this.ref.detectChanges(); // re-render HTML
  }

  // cross references the fishery notices retrieved from the common list with the user's notice metadata
  updateNoticeData(): void {
    this.noticeMetadata = this.noticeMetadata.filter((md: FisheryNotice) => this.fisheryNotices.find((fn: FisheryNotice) => fn.noticeNumber == md.noticeNumber));
    console.log(this.noticeMetadata)
    UserStorageService.saveNoticeMetadata(this.noticeMetadata);
    for (let data of this.noticeMetadata) {
      for (let notice of this.fisheryNotices) {
        if (notice.noticeNumber === data.noticeNumber) {
          notice.isSaved = data.isSaved;
          notice.isRead = data.isRead;
        }
      }
    }
  }

  //Check which notices in the metadata are saved but not in the fishery notices and put them
  putSaved(): void {
    this.noticeMetadata.filter((meta: FisheryNotice) => meta.isSaved && 
      !this.fisheryNotices.find((notice: FisheryNotice) => notice.noticeNumber == meta.noticeNumber)
    ).forEach((data: FisheryNotice) => this.fisheryNotices.push({...data}));
  }

  // returns the appropriate class for the notice depending on whether it's been read
  determineNoticeStyle(notice: FisheryNotice): string {

    if (notice.isRead) {
      return 'panel-title-read';
    } 

    return 'panel-title-unread';

  }

  // parses each fishery notice body to allow it to be displayed with proper formatting
  parseNotices(): void {

    for (let notice of this.fisheryNotices) {
      notice.bodyHTML = this.createHyperlinks(notice.bodyHTML); // replace text links and emails with hyperlinks
      notice.bodyHTML = notice.bodyHTML.replace(/\n/g, '<br>'); // include line breaks where appropriate
    }

  }


  /*  ---------- Saving & Selecting Notices  ---------- */


  // updates a notice's selected status
  updateSelectedNotice(notice: FisheryNotice): void {

     // update the notice metadata 
     let noticeFound: boolean = false;
     for (let noticeData of this.noticeMetadata) {
       if (notice.noticeNumber === noticeData.noticeNumber) { // if the notice is already in the metadata array
         noticeData.isRead = true;
         noticeFound = true;
         break;
       }
     }
     if (!noticeFound) { // if the notice is not yet in the metadata array
       let newNoticeMetadata: FisheryNotice = new FisheryNotice(notice.subject);
       newNoticeMetadata.noticeNumber = notice.noticeNumber;
       newNoticeMetadata.isRead = true;
       this.noticeMetadata.push(newNoticeMetadata);
     }

  }


  // invoked whenever a notice is clicked - displays the notice body
  noticeSelected(notice: FisheryNotice): void {
    
    // update the UI
    window.scrollTo({
      top: 0,
      left: 0,
    }); // TO-DO: make scrolling instant - behavior:'instant' doesn't work

    this.showCurrNotice = !this.showCurrNotice;
    this.currNotice = notice;

    if (!notice.isRead) {
     this.updateSelectedNotice(notice); // update the notice's selected status
     UserStorageService.saveNoticeMetadata(this.noticeMetadata);
    }

    localStorage.setItem('ViewedNotice', notice.noticeNumber);
    notice.isRead = true;

  }


  // invoked whenever the user clicks on the star icon for a notice
  favoriteNotice(notice: FisheryNotice = this.currNotice): void {

    let noticeFound: number = this.noticeMetadata.findIndex((note: FisheryNotice) => note.noticeNumber == notice.noticeNumber);

    notice.isSaved = !notice.isSaved; // update the notice state for the UI
    
    //If the notice was found in the metadata, i.e. was unsaved, we check if it should be archived and if so remove it entirely from the app local storage
    if (noticeFound > -1) {
      if (this.noticeMetadata[noticeFound].isSaved) {
        let archiveDate = new Date(notice.receivedTime);
        archiveDate.setMonth(archiveDate.getMonth() + 2);
        console.log(archiveDate < new Date())
        if (archiveDate < new Date()) {
          //Remove the notice entirely
          this.noticeMetadata = this.noticeMetadata.filter((md: FisheryNotice) => md.noticeNumber != notice.noticeNumber);
          this.fisheryNotices = this.fisheryNotices.filter((fn: FisheryNotice) => fn.noticeNumber != notice.noticeNumber);
          UserStorageService.removeNoticeMetadata(notice);
          return;
        } else {
          this.noticeMetadata[noticeFound].bodyHTML = "";
          this.noticeMetadata[noticeFound].isSaved = notice.isSaved;
        }
      } else {
          this.noticeMetadata[noticeFound] = { ...notice};
      }  
    } else {
      let newNoticeMetadata: FisheryNotice = { ...notice};
      this.noticeMetadata.push(newNoticeMetadata);
    }

    UserStorageService.saveNoticeMetadata(this.noticeMetadata); 
  }


  /*  ---------- Checking For New Notices  ---------- */


  // checks if the user is online, then invokes the method to retrieve notices if they are
  async checkForNewNotices(): Promise<void> {

    this.isDataLoaded = false;

    if (this.onlineStatusService.getStatus() === OnlineStatusType.OFFLINE) {
      this.sharedMethodsService.toastUnableToCheckNotices();
      this.isDataLoaded = true;
      return;
    }

    await this.getNoticesFromCouch();

  }

   // pulls down any new notices from the couch db and displays them in the app
  async getNoticesFromCouch(): Promise<void> {
    FisheryNoticesService.checkForNotices(this.userDetails.userEmail).then((res: boolean) => {
      if (res) {
        UserStorageService.getNoticeMetadata().then(() => {
          FisheryNoticesService.getAllNoticeDocs(this.userDetails.userEmail).then(() => {
            this.initialize();
            this.sharedMethodsService.toastNewNoticesReceived();
          });
        });
      } else {
        this.sharedMethodsService.toastUnableToRetrieveNotices();
        this.isDataLoaded = true;
      } 
    });

  }


  /*  ---------- Inbox Methods  ---------- */


  // invoked when the 'back' button gets selected to return to the inbox list
  backToInboxList(): void {

    if (this.showCurrNotice) { // if we are viewing a notice
      this.showCurrNotice = false;
      localStorage.removeItem('ViewedNotice');
    } else { // if we are viewing an inbox
      this.showAllNotices = this.showSavedNotices = this.showGillNetNotices = 
      this.showSeineNotices = this.showTrollNotices = false;
      this.showInboxList = true;
      localStorage.removeItem('ViewedInbox');
    }

  }

  // invoked when the user clicks on a particular inbox
  inboxClick(inboxType: string): void {

    this.showInboxList = false; // hide the inbox list

    // update flags to ensure the correct inbox is displayed
    switch (inboxType) {
      case 'allNotices':
        this.showAllNotices = true;
        break;
      case 'savedNotices':
        this.showSavedNotices = true;
        break;
      case 'Gill Net':
        this.showGillNetNotices = true;
        break;
      case 'Seine':
        this.showSeineNotices = true;
        break;
      case 'Troll':
        this.showTrollNotices = true;
        break;
    }
    localStorage.setItem('ViewedInbox', inboxType);
  }

  // returns the name of the inbox currently being displayed
  getInboxName(): string {

    if (this.showAllNotices) {
      return 'All Notices';
    }
    if (this.showSavedNotices) {
      return 'Saved Notices';
    }
    if (this.showGillNetNotices) {
      return 'Gill Net Notices';
    }
    if (this.showSeineNotices) {
      return 'Seine Notices';
    }
    if (this.showTrollNotices) {
      return 'Troll Notices';
    }

    return '';

  }


  /*  ---------- Helper Methods  ---------- */


  // parses the email body HTML and replaces text links & email addresses with hyperlinks
  /* Credit to: https://stackoverflow.com/questions/1500260/detect-urls-in-text-with-javascript */
  createHyperlinks(body: string): string {

    let regexURL: RegExp = /(https?:\/\/[^\s]+)/g;
    let regexEmail: RegExp = /(\b[\w.-]+@[\w.-]+\.\w+\b)/g; // credit to chatGPT
    let newBody: string = '';
    
    newBody = body.replace(regexEmail, '<a href="mailto:$1">$1</a>'); 
    newBody = newBody.replace(regexURL, '<a href="$1" target="_blank">$1</a>');

    return newBody;

  }

  // returns the number of unread notices in the specified mailbox
  getUnreadNotices(mailbox: string): number {

    let counter: number = 0;

    for (let notice of this.fisheryNotices) {
      if (!notice.isRead &&
      ((mailbox === 'all') ||
      (mailbox === 'saved' && notice.isSaved) ||
      (mailbox === 'gill net' && notice.gillNet) ||
      (mailbox === 'seine' && notice.seine) ||
      (mailbox === 'troll' && notice.troll))) {
        counter++;
      }
    }

    return counter;

  }

  // method that evaluates whether or not a particular notice should be displayed in the inbox list
  showNotice(notice: FisheryNotice): boolean {

    let condt1: boolean = this.showSavedNotices && notice.isSaved;
    let condt2: boolean = this.showGillNetNotices && notice.gillNet;
    let condt3: boolean = this.showSeineNotices && notice.seine;
    let condt4: boolean = this.showTrollNotices && notice.troll;

    return this.showAllNotices || condt1 || condt2 || condt3 || condt4;
   
  }


  ngOnDestroy(): void {
    localStorage.removeItem('ViewedInbox');
    localStorage.removeItem('ViewedNotice');
  }

}
